import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MapPin, ChevronRight, Search, Compass, Sparkles, Star, Gift } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-blue-400", "bg-cyan-400", "bg-teal-400", "bg-indigo-400", "bg-violet-400", "bg-sky-500", "bg-blue-500"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-10 h-10 text-sm rounded-2xl" : size === "lg" ? "w-16 h-16 text-xl rounded-3xl" : "w-12 h-12 text-base rounded-2xl";
  return <div className={`${s} ${colors[i]} flex items-center justify-center flex-shrink-0 shadow-lg shadow-sky-500/20`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const StoryCard = ({ offer, onClick, isFavorited, onFav }) => (
  <motion.div
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className="flex-shrink-0 w-36 cursor-pointer group"
    data-testid={`radar-story-${offer.business_id}`}
  >
    <div className="relative">
      <div className="w-full aspect-square rounded-3xl bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.08)] flex flex-col items-center justify-center p-3">
        <MiniLogo name={offer.name} size="lg" />
        <p className="text-xs font-semibold text-slate-700 mt-2 truncate w-full text-center">{offer.name}</p>
        {getStatus(offer.business_id) && <div className="flex justify-center mt-0.5"><UnifiedStatusBadge status={getStatus(offer.business_id)} /></div>}
        <p className="text-lg font-bold text-sky-600">${offer.offer_value_est}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onFav(offer.business_id); }}
        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm"
      >
        <Heart className={`w-3.5 h-3.5 ${isFavorited ? "fill-red-500 text-red-500" : "text-slate-300"}`} />
      </button>
    </div>
  </motion.div>
);

export const RadarView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite, categories, selectedCategory, setSelectedCategory, searchQuery, setSearchQuery } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  // Group by category for "story rows"
  const grouped = {};
  offers.forEach(o => {
    const cat = o.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(o);
  });
  const catRows = Object.entries(grouped).sort((a, b) => b[1].length - a[1].length).slice(0, 5);

  return (
    <div className="min-h-screen pb-28 relative overflow-hidden" style={{ fontFamily: "'Fredoka', sans-serif", background: "linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 30%, #ffffff 60%)" }}>
      {/* Animated radar pulse bg */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.8], opacity: [0.15, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeOut" }}
          className="absolute inset-0 rounded-full border-2 border-sky-300"
        />
        <motion.div
          animate={{ scale: [1, 1.5], opacity: [0.1, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeOut", delay: 1 }}
          className="absolute inset-0 rounded-full border border-sky-200"
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-sky-400 shadow-lg shadow-sky-400/50" />
      </div>

      {/* Header */}
      <div className="relative z-10 px-6 pt-8 pb-4">
        <div className="flex items-center gap-3">
          <Compass className="w-6 h-6 text-sky-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Party Radar</h1>
            <p className="text-sm text-sky-500">{offers.length} birthday deals detected nearby</p>
          </div>
        </div>
      </div>

      {/* Countdown bubble */}
      <div className="relative z-10 px-6 mb-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/70 backdrop-blur-xl rounded-3xl p-5 border border-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-xs font-medium">Birthday countdown</p>
              <p className="text-3xl font-bold text-slate-800 mt-1">{stats.days_until_birthday} <span className="text-base font-medium text-slate-400">days</span></p>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs font-medium">Total haul</p>
              <p className="text-2xl font-bold text-sky-600 mt-1">${totalValue.toFixed(0)}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search */}
      <div className="relative z-10 px-6 mb-5">
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/60 shadow-sm flex items-center gap-2">
          <Search className="w-4 h-4 text-sky-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Discover birthday deals..."
            className="flex-1 text-sm outline-none bg-transparent text-slate-700 placeholder-slate-400"
            data-testid="radar-search"
          />
        </div>
      </div>

      {/* Expiring "nearby" */}
      {expiringSoon.length > 0 && (
        <div className="relative z-10 mb-5">
          <div className="px-6 flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <h2 className="text-sm font-bold text-slate-700">Expiring Nearby</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide">
            {expiringSoon.slice(0, 5).map(offer => (
              <StoryCard key={offer.business_id} offer={offer} onClick={() => onOfferClick(offer)} isFavorited={favoriteIds.has(offer.business_id)} onFav={toggleFavorite} />
            ))}
          </div>
        </div>
      )}

      {/* Category story rows */}
      {catRows.map(([cat, catOffers]) => (
        <div key={cat} className="relative z-10 mb-5">
          <div className="px-6 flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-700">{cat}</h2>
            <span className="text-xs text-sky-500">{catOffers.length} spots</span>
          </div>
          <div className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide">
            {catOffers.slice(0, 6).map(offer => (
              <StoryCard key={offer.business_id} offer={offer} onClick={() => onOfferClick(offer)} isFavorited={favoriteIds.has(offer.business_id)} onFav={toggleFavorite} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
